import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { UnauthenticatedError } from '../utils/auth';
import { Destination, DestinationMetadataApiClient, DestinationProviderApiClient } from './DestinationsApiClient';
import { HTTPRequestInfo } from '../types/obs';
import { enableStreamDestination } from '../api/enableDestination';

const refreshAccessTokenPromiseMap = new Map<string, ReturnType<typeof enableStreamDestination>>();

export class KickApiClient implements DestinationProviderApiClient<'kick'> {
  static CLIENT_ID = '01JH2SSCETWR1B5X3Q4M1352D1';
  private authContextProvider: AuthContextProvider;
  private serviceEnvironment: ServiceEnvironment;
  private destinationMetadata: Destination;
  constructor({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  }: {
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
    destinationMetadata: Destination;
  }) {
    this.authContextProvider = authContextProvider;
    this.serviceEnvironment = serviceEnvironment;
    this.destinationMetadata = destinationMetadata;
  }

  private async refreshAccessToken() {
    const refreshAccessTokenPromise = refreshAccessTokenPromiseMap.get(this.destinationMetadata.id);

    if (refreshAccessTokenPromise) {
      return refreshAccessTokenPromise;
    }

    const result = enableStreamDestination({
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
      streamDestination: this.destinationMetadata,
      source: { id: this.destinationMetadata.id },
    });

    refreshAccessTokenPromiseMap.set(this.destinationMetadata.id, result);

    // Invalidate the cache after 5 minutes
    setTimeout(
      () => {
        refreshAccessTokenPromiseMap.delete(this.destinationMetadata.id);
      },
      1000 * 60 * 5
    );

    return result;
  }

  public async getAuthHeaders() {
    const {
      channel: { accessToken },
    } = await this.authContextProvider.getAuthContext();

    return {
      Authorization: `Bearer ${this.destinationMetadata.accessToken || accessToken}`,
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    options: Omit<HTTPRequestInfo, 'url' | 'headers'> & {
      params?: { [k: string]: unknown };
      headers?: Record<string, string>;
    } = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    {
      refreshAccessTokenRetries = 3,
    }: {
      refreshAccessTokenRetries?: number;
    } = {}
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    const searchParams = new URLSearchParams();

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value === undefined) {
          return;
        }

        searchParams.append(key, `${value}`);
      });
    }

    const url = new URL('https://api.kick.com');
    url.pathname = endpoint;
    url.search = searchParams.toString();

    const response = await fetch(url.toString(), {
      ...options,
      method: options.method || 'GET',
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (response.ok) {
      // if (response.success) {
      try {
        const json = await response.json();
        // const json = JSON.parse(response.body);
        return json as T;
      } catch (err) {
        // Api returned 200 but no json was returned
        return undefined as never;
      }
    }

    if (response.status === 401) {
      if (refreshAccessTokenRetries === 0) {
        throw new UnauthenticatedError('Unauthenticated');
      }

      const result = await this.refreshAccessToken();
      this.destinationMetadata = result;
      return this.makeRequest(endpoint, options, {
        refreshAccessTokenRetries: refreshAccessTokenRetries - 1,
      });
    }

    throw new Error(`Failed to fetch ${endpoint}`);
  }

  public async fetchStreamKey() {
    const response = await this.makeRequest<{
      data: {
        stream: {
          key: string;
          url: string;
        };
      }[];
    }>('/public/v1/channels');

    return response.data[0].stream.key;
  }

  public async getStreamingOutputInfo() {
    const response = await this.makeRequest<{
      data: {
        stream: {
          key: string;
          url: string;
        };
      }[];
    }>('/public/v1/channels');

    return {
      streamKey: response.data?.[0]?.stream?.key ?? '',
      serverUrl: response.data?.[0]?.stream?.url ?? '',
    };
  }

  public async updateChannelMetadata({ title, gameId }: { title?: string; gameId?: string }) {
    const gameIdInt = gameId ? parseInt(gameId, 10) : undefined;

    const body: { stream_title?: string; category_id?: number } = {};
    const titleTrimmed = title?.trim();

    if (titleTrimmed) {
      body.stream_title = titleTrimmed;
    }

    if (gameIdInt) {
      body.category_id = gameIdInt;
    }

    const response = await this.makeRequest('/public/v1/channels', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    return response;
  }

  public setupStreamRoom: DestinationProviderApiClient<'kick'>['setupStreamRoom'] = async (params) => {
    const { streamKey, serverUrl } = await this.getStreamingOutputInfo();

    await this.updateChannelMetadata({
      title: params.title,
      gameId: params.categoryId,
    }).catch(console.error);

    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      destinationMetadata: this.destinationMetadata,
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
    });

    return destinationMetadataApiClient.updateDestination({
      outputs: this.destinationMetadata.outputs
        .map((output) => {
          output.streamingSettings.streamKey = streamKey;
          output.streamingSettings.serverUrl = serverUrl;
          output.streamingSettings.type = 'rtmp_common';
          output.auxiliaryData = {
            ...output.auxiliaryData,
            isPrimaryOutput: output.auxiliaryData?.isPrimaryOutput ?? false,
            destinationId: this.destinationMetadata.id,
            streamTitle: params.title,
            streamCategory: params.categoryId,
          };
          return output;
        })
        .slice(0, 1),
    });
  };

  public async updateStreamRoom(params: {
    outputId: string;
    title?: string;
    categoryId?: string;
    privacyStatus?: string;
  }): Promise<void> {
    await this.updateChannelMetadata({
      title: params.title,
      gameId: params.categoryId,
    }).catch(console.error);
  }

  public async getCategories(params: { query: string }): Promise<Map<string, string>> {
    const response = (await this.makeRequest<{ data: { id: string; name: string; thumbnail: string }[] }>(
      '/public/v1/categories',
      {
        method: 'GET',
        params: {
          q: params.query,
          limit: 10,
        },
      }
    )) ?? { data: [] };

    return new Map(response.data.map((category) => [category.id.toString(), category.name]));
  }

  public async getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null> {
    const response = await this.makeRequest<{ data: { id: string; name: string } }>(
      `/public/v1/categories/${params.categoryId}`
    ).catch(() => null);

    return response ? { id: response.data.id.toString(), displayName: response.data.name } : null;
  }

  public async endBroadcast(): Promise<void> {
    void 0;
  }
}
